import { formatCredits } from "../utils";

/**
 * A credit figure with its sign and its unit, laid out as one run.
 *
 * The formatters already isolate the digits, which is enough for a bare number or a
 * date. It is NOT enough for an amount assembled in JSX:
 *
 *     -{formatCredits(spent)} cr
 *
 * There the minus and the "cr" are separate neutral runs either side of an isolated
 * number, and in an Arabic paragraph the bidi algorithm reorders them around it —
 * "-18 cr" renders as "cr 18-". The figure is right and reads as wrong, which on a
 * page about money is the same thing.
 *
 * `unicode-bidi: isolate` with an explicit `direction: ltr` on the wrapper makes the
 * whole sign-number-unit run one left-to-right island inside the Arabic sentence,
 * without disturbing the sentence around it.
 *
 * A deduction of nothing prints "0", never "-0" — the same rule the statement PDF
 * applies, for the same reason.
 */
export default function Amount({
  value,
  sign = "auto",
  unit,
  className,
}: {
  value: number;
  /** "auto" prints a minus only for a negative; "deducted" prints one for a positive
   *  spend; "plus" prints a leading + for an inflow. */
  sign?: "auto" | "deducted" | "plus";
  unit?: string;
  className?: string;
}) {
  // A money component must never print "NaN cr". It happened on the review screen
  // when a page ran briefly against an older payload that did not carry the field
  // yet — the figure was undefined, the arithmetic produced NaN, and the screen
  // stated it in the same confident type as a real balance. An absent number is
  // "—": visibly missing, which a reader can question, rather than a number-shaped
  // thing that is not a number.
  if (!Number.isFinite(value)) {
    return <span className={`amount${className ? ` ${className}` : ""}`}>—</span>;
  }

  const magnitude = sign === "deducted" ? Math.abs(value) : value;
  let prefix = "";
  if (sign === "deducted" && magnitude !== 0) prefix = "-";
  if (sign === "plus" && value > 0) prefix = "+";

  return (
    <span className={`amount${className ? ` ${className}` : ""}`}>
      {prefix}{formatCredits(magnitude)}{unit ? ` ${unit}` : ""}
    </span>
  );
}
